/**
 * Interface commune pour tous les adapters d'imprimante
 * Architecture pluggable pour supporter différents modèles
 */

export interface PrintJob {
  lines: PrintLine[];
  metadata?: {
    saleNumber?: string;
    vendorName?: string;
    timestamp?: Date;
  };
}

export interface PrintColumn {
  text: string;
  width: number; // 0-1 (proportion)
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
}

export interface PrintLine {
  type: 'text' | 'separator' | 'barcode' | 'qrcode' | 'image' | 'feed' | 'columns' | 'cut';
  content?: string;
  char?: string; // Pour type 'separator' - caractère à répéter
  columns?: PrintColumn[]; // Pour type 'columns'
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  fontSize?: 'small' | 'normal' | 'large' | 'xlarge';
  underline?: boolean;
  feeds?: number; // Pour type 'feed'
  size?: number; // Pour type 'qrcode' - taille du module (1-16)
}

export interface PrinterConfig {
  model: 'EPSON_TM_T20' | 'EPSON_TM_M30' | 'EPSON_TM_M267D' | 'EPSON_TM_T88' | 'STAR_TSP100' | 'BROWSER_PRINT';
  connection: 'USB' | 'BLUETOOTH' | 'NETWORK' | 'BROWSER';
  settings?: {
    ip?: string; // Pour connexion réseau
    port?: number;
    characterSet?: string;
    codePage?: number;
  };
}

export interface PrinterStatus {
  connected: boolean;
  online: boolean;
  paperStatus: 'ok' | 'low' | 'empty' | 'unknown';
  error?: string;
}

export interface PrinterAdapter {
  /**
   * Nom du modèle d'imprimante
   */
  readonly modelName: string;

  /**
   * Type de connexion supportée
   */
  readonly connectionType: 'USB' | 'BLUETOOTH' | 'NETWORK' | 'BROWSER' | 'CUPS' | 'LOCAL_BRIDGE';

  /**
   * Connecter l'imprimante
   */
  connect(): Promise<void>;

  /**
   * Déconnecter l'imprimante
   */
  disconnect(): Promise<void>;

  /**
   * Vérifier si l'imprimante est connectée
   */
  isConnected(): boolean;

  /**
   * Obtenir le statut de l'imprimante
   */
  getStatus(): Promise<PrinterStatus>;

  /**
   * Imprimer un ticket
   */
  print(job: PrintJob): Promise<void>;

  /**
   * Ouvrir le tiroir-caisse (si supporté)
   */
  openCashDrawer?(): Promise<void>;

  /**
   * Couper le papier (si supporté)
   */
  cut?(): Promise<void>;
}

/**
 * Erreurs spécifiques aux imprimantes
 */
export class PrinterError extends Error {
  constructor(
    message: string,
    public code:
      | 'NOT_SUPPORTED'
      | 'NOT_CONNECTED'
      | 'PAPER_EMPTY'
      | 'PRINT_ERROR'
      | 'CONNECTION_ERROR'
      | 'CONFIG_NOT_FOUND'
      | 'PRINTER_NOT_FOUND',
    public originalError?: Error
  ) {
    super(message);
    this.name = 'PrinterError';
  }
}
